import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /**
   * When this value changes the boundary clears its caught error. Pass
   * the route path so navigating away from a broken view recovers the UI
   * without a full reload.
   */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time exceptions in its subtree and shows a recoverable
 * fallback instead of letting one bad component white-screen the whole
 * panel. Added after a single undefined field in a pipeline-produced
 * payload (risk_assessment.touches_protected_paths) took down the entire
 * TaskDetail page — unacceptable for unsupervised multi-developer use.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props): void {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: unknown): void {
    // No remote error sink yet — surface to the browser console so the
    // operator can retrieve the stack from a developer's report.
    // eslint-disable-next-line no-console
    console.error('[panel] render error caught by ErrorBoundary:', error, info);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="max-w-2xl mx-auto mt-8 rounded-lg border-2 border-red-300 bg-red-50 p-5">
          <h2 className="font-semibold text-red-900 text-lg mb-1">
            Something went wrong displaying this page
          </h2>
          <p className="text-sm text-red-800 mb-3">
            The panel hit an unexpected error while rendering this view. Your
            task and its data are safe — this is a display problem, not a lost
            task. Reload to retry; if it keeps happening, copy the detail below
            for whoever runs the pipeline.
          </p>
          <pre className="text-xs bg-white border border-red-200 rounded p-2 overflow-auto max-h-48 text-red-900 whitespace-pre-wrap">
            {this.state.error.message}
          </pre>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-3 py-1.5 text-sm rounded bg-red-600 text-white hover:bg-red-700"
            >
              Reload
            </button>
            <button
              type="button"
              onClick={() => this.setState({ error: null })}
              className="px-3 py-1.5 text-sm rounded border border-red-300 text-red-700 hover:bg-red-100"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
